<template>

    <!-- BEGIN Base Outer Element -->
    <div id="main-player-view" class="flex flex-col h-full w-full bg-theme-level4">

        <!-- BEGIN Video Player -->
        <video-player></video-player>
        <!-- END Video Player -->

        <!-- BEGIN CLOSE BUTTON -->
        <div class="absolute rounded-full pt-1 pl-1" style="top: 66px; right: 0; width: 46px; height: 46px; z-index: 999;">
            <i class="material-icons text-theme-control-label cursor-pointer bg-black rounded-full" @click="closePlayer" style="font-size: 38px;">highlight_off</i>
        </div>
        <!-- END CLOSE BUTTON -->

        <!-- BEGIN Fixed Section -->
        <div v-text="this.$root.shared.playerPayload.title"
             class="text-xl text-theme-main font-bold px-2 pt-2 mb-2 leading-tight"></div>
        <!-- END Fixed Section -->

        <!-- BEGIN Player View Data Container -->
        <div class="h-full w-full overflow-y-auto scrolling-touch" style="height: 100vh;">


            <!-- BEGIN Data Section -->

            <!-- BEGIN Publishing Date -->
            <div id="content-date" class="flex flex-row items-center text-sm text-theme-discrete mt-2 px-2">
                <i class="material-icons mr-2" style="font-size: 16px;">event_note</i>
                <span class="flex-1">{{  translate('published-on') }} <span v-text="this.$root.shared.playerPayload.publish_date.split(' ')[0]"></span>, {{  translate('at') }} <span v-text="this.$root.shared.playerPayload.publish_date.split(' ')[1]"></span></span>
            </div>
            <!-- END Publishing Date -->

            <!-- BEGIN Abstract and Toggle switch -->
            <div id="data-container" class="flex flew-row items-start mb-2 w-full">

                <div class="text-sm text-theme-discrete p-2 leading-normal" v-text="this.$root.shared.playerPayload.abstract"></div>

                <i class="material-icons mr-2 ml-2 transform text-theme-discrete transition-all duration-200" :class="{ 'rotate-90': !dataIsVisible }" style="font-size: 32px;" @click="toggleDataIsVisible">arrow_drop_down</i>

            </div>
            <!-- END Abstract and Toggle switch -->

            <!-- BEGIN Toggleable Area -->
            <div :class="{ 'hidden': !dataIsVisible }" class="flex flex-col px-2">

                <span class="text-xs text-theme-discrete">{{ translate('playerview-correspondent') }}:</span>

                <span v-if="this.$root.shared.playerCategories[5] !== ''" class="text-xs text-theme-discrete flex flex-row flex-wrap mb-2">
                    <span
                          class="text-theme-main text-sm py-1 mr-2 mt-1"
                          v-text="this.$root.shared.playerCategories[5]">
                    </span>
                </span>
                <span v-else class="flex flex-row flex-wrap mb-2">
                    <p class="text-theme-muted text-xs mt-1">{{ translate('playerview-no-correspondent') }}</p>
                </span>

                <span class="text-xs text-theme-discrete">{{ translate('playerview-pois') }}:</span>

                <span v-if="this.$root.shared.playerCategories[2].length !== 0" class="text-xs text-theme-discrete flex flex-row flex-wrap mb-2">
                    <span v-for="person in this.$root.shared.playerCategories[2]"
                          class="text-theme-main text-sm py-1 mr-2 mt-1"
                          v-text="person">
                    </span>
                </span>
                <span v-else class="flex flex-row flex-wrap mb-2">
                    <p class="text-theme-muted text-xs mt-1">{{ translate('playerview-no-poi') }}</p>
                </span>


                <span class="text-xs text-theme-discrete mt-2">{{ translate('playerview-categories') }}:</span>

                <span class="text-xs text-theme-discrete flex flex-row flex-wrap mb-2">
                    <span v-for="category in this.$root.shared.playerCategories[0]"
                          class="bg-theme-controls3 text-theme-control-label text-xs rounded-full px-2 py-1 mr-1 mt-1"
                          v-text="category">
                    </span>
                </span>


                <span class="text-xs text-theme-discrete mt-2">{{ translate('playerview-subcategories') }}:</span>

                <span v-if="this.$root.shared.playerCategories[3].length !== 0" class="text-xs text-theme-discrete flex flex-row flex-wrap mb-2">
                    <span v-for="subcategory in this.$root.shared.playerCategories[3]"
                          class="bg-theme-controls3 text-theme-control-label text-xs rounded-full px-2 py-1 mr-1 mt-1"
                          v-text="subcategory">
                    </span>
                </span>
                <span v-else class="flex flex-row flex-wrap mb-2">
                    <p class="text-theme-muted text-xs mt-1">{{ translate('playerview-no-subcat') }}</p>
                </span>


                <span class="text-xs text-theme-discrete mt-2">{{ translate('playerview-countries') }}:</span>

                <span v-if="this.$root.shared.playerCategories[4].length !== 0" class="text-xs text-theme-discrete flex flex-row flex-wrap mb-2">
                    <span v-for="country in this.$root.shared.playerCategories[4]"
                          class="bg-theme-copy4 text-theme-control-label text-xs rounded px-2 py-1 mr-1 mt-1"
                          v-text="country">
                    </span>
                </span>
                <span v-else class="flex flex-row flex-wrap mb-2">
                    <p class="text-theme-muted text-xs mt-1">{{ translate('playerview-no-country') }}</p>
                </span>


                <span class="text-xs text-theme-discrete mt-2">{{ translate('playerview-tags') }}:</span>

                <span v-if="this.$root.shared.playerCategories[1].length !== 0" class="text-xs text-theme-discrete flex flex-row flex-wrap mb-2">
                    <span v-for="tag in this.$root.shared.playerCategories[1]"
                          class="bg-theme-copy4 text-theme-control-label text-xs rounded px-2 py-1 mr-1 mt-1"
                          v-text="tag">
                    </span>
                </span>
                <span v-else class="flex flex-row flex-wrap mb-2">
                    <p class="text-theme-muted text-xs mt-1">{{ translate('playerview-no-tag') }}</p>
                </span>

            </div>
            <!-- END Toggleable Area -->

            <!-- BEGIN Stats and Sharing -->
            <div class="flex flex-row items-center justify-between mt-2 px-2 border-b border-t border-theme-level2 pb-2 pt-2">

                <span class="text-sm text-theme-discrete flex flex-row items-center"><span v-text="this.$root.shared.contentPlays"></span>
                    <i class="material-icons mr-2 pl-1 cursor-pointer w-6 h-6 text-theme-discrete hover:text-theme-main">visibility</i>
                </span>

                <div class="flex flex-row items-center">
                    <i class="material-icons pl-1 cursor-pointer w-6 h-6 text-theme-discrete hover:text-theme-main">thumb_up</i>
                    <p class="mr-1 ml-2 text-sm text-green-500" v-text="this.$root.shared.contentLikes"></p>
                    <i class="material-icons ml-1 mr-1 cursor-pointer w-6 h-6 text-theme-discrete hover:text-theme-main">thumb_down</i>
                    <p class="text-sm text-red-500" v-text="this.$root.shared.contentDislikes"></p>
                </div>

                <div class="flex flex-row justify-between items-center">

                    <!-- FACEBOOK SHARE -->
                    <span @click.stop="">
                        <ShareNetwork
                            network="facebook"
                            :url="shareableLinkCode"
                            :title="this.$root.shared.playerPayload.title"
                            :description="this.$root.shared.playerPayload.abstract"
                        >
                            <i class="material-icons mr-1 pl-2 pt-2 cursor-pointer w-10 h-10 icon-circled text-theme-discrete hover:text-theme-main" style="height: 34px; width: 34px; padding-left:7px; padding-top: 7px;">
                                <svg class="inline-svg-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18px" height="18px">
                                    <path d="M17.525,9H14V7c0-1.032,0.084-1.682,1.563-1.682h1.868v-3.18C16.522,2.044,15.608,1.998,14.693,2 C11.98,2,10,3.657,10,6.699V9H7v4l3-0.001V22h4v-9.003l3.066-0.001L17.525,9z"/>
                                </svg>
                            </i>
                        </ShareNetwork>
                    </span>


                    <!-- TWITTER SHARE -->
                    <span @click.stop="">
                        <ShareNetwork
                            network="twitter"
                            :url="shareableLinkCode"
                            :title="this.$root.shared.playerPayload.title"
                            hashtags="telesurtv"
                            twitter-user="telesurtv"
                        >
                            <i class="material-icons mr-1 cursor-pointer icon-circled text-theme-discrete hover:text-theme-main" style="height: 34px; width: 34px; padding-left:6px; padding-top: 6px;">
                                <svg class="inline-svg-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18px" height="18px">
                                    <path d="M28,6.937c-0.957,0.425-1.985,0.711-3.064,0.84c1.102-0.66,1.947-1.705,2.345-2.951c-1.03,0.611-2.172,1.055-3.388,1.295 c-0.973-1.037-2.359-1.685-3.893-1.685c-2.946,0-5.334,2.389-5.334,5.334c0,0.418,0.048,0.826,0.138,1.215 c-4.433-0.222-8.363-2.346-10.995-5.574C3.351,6.199,3.088,7.115,3.088,8.094c0,1.85,0.941,3.483,2.372,4.439 c-0.874-0.028-1.697-0.268-2.416-0.667c0,0.023,0,0.044,0,0.067c0,2.585,1.838,4.741,4.279,5.23 c-0.447,0.122-0.919,0.187-1.406,0.187c-0.343,0-0.678-0.034-1.003-0.095c0.679,2.119,2.649,3.662,4.983,3.705 c-1.825,1.431-4.125,2.284-6.625,2.284c-0.43,0-0.855-0.025-1.273-0.075c2.361,1.513,5.164,2.396,8.177,2.396 c9.812,0,15.176-8.128,15.176-15.177c0-0.231-0.005-0.461-0.015-0.69C26.38,8.945,27.285,8.006,28,6.937z"/>
                                </svg>
                            </i>
                        </ShareNetwork>
                    </span>


                    <!-- WHATSAPP SHARE -->
                    <span @click.stop="">
                        <ShareNetwork
                            network="whatsapp"
                            :url="shareableLinkCode"
                            :title="this.$root.shared.playerPayload.title"
                            :description="this.$root.shared.playerPayload.abstract"
                        >
                            <i class="material-icons mr-1 pl-2 pt-2 cursor-pointer icon-circled text-theme-discrete hover:text-theme-main" style="height: 34px; width: 34px; padding-left:8px; padding-top: 7px;">
                                <svg class="inline-svg-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18px" height="18px">
                                    <path d="M19.077,4.928C17.191,3.041,14.683,2.001,12.011,2c-5.506,0-9.987,4.479-9.989,9.985 c-0.001,1.76,0.459,3.478,1.333,4.992L2,22l5.233-1.237c1.459,0.796,3.101,1.215,4.773,1.216h0.004 c5.505,0,9.986-4.48,9.989-9.985C22.001,9.325,20.963,6.816,19.077,4.928z M16.898,15.554c-0.208,0.583-1.227,1.145-1.685,1.186 c-0.458,0.042-0.887,0.207-2.995-0.624c-2.537-1-4.139-3.601-4.263-3.767c-0.125-0.167-1.019-1.353-1.019-2.581 S7.581,7.936,7.81,7.687c0.229-0.25,0.499-0.312,0.666-0.312c0.166,0,0.333,0,0.478,0.006c0.178,0.007,0.375,0.016,0.562,0.431 c0.222,0.494,0.707,1.728,0.769,1.853s0.104,0.271,0.021,0.437s-0.125,0.27-0.249,0.416c-0.125,0.146-0.262,0.325-0.374,0.437 c-0.125,0.124-0.255,0.26-0.11,0.509c0.146,0.25,0.646,1.067,1.388,1.728c0.954,0.85,1.757,1.113,2.007,1.239 c0.25,0.125,0.395,0.104,0.541-0.063c0.146-0.166,0.624-0.728,0.79-0.978s0.333-0.208,0.562-0.125s1.456,0.687,1.705,0.812 c0.25,0.125,0.416,0.187,0.478,0.291C17.106,14.471,17.106,14.971,16.898,15.554z"/>
                                </svg>
                            </i>
                        </ShareNetwork>
                    </span>


                    <!-- YOUTUBE CHANNEL -->
                    <!--<i class="material-icons mr-1 pl-2 pt-2 cursor-pointer icon-circled text-theme-discrete hover:text-theme-main" style="height: 34px; width: 34px; padding-left:8px; padding-top: 8px;">
                        <svg class="inline-svg-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18px" height="18px">
                            <path d="M21.582,6.186c-0.23-0.86-0.908-1.538-1.768-1.768C18.254,4,12,4,12,4S5.746,4,4.186,4.418 c-0.86,0.23-1.538,0.908-1.768,1.768C2,7.746,2,12,2,12s0,4.254,0.418,5.814c0.23,0.86,0.908,1.538,1.768,1.768 C5.746,20,12,20,12,20s6.254,0,7.814-0.418c0.861-0.23,1.538-0.908,1.768-1.768C22,16.254,22,12,22,12S22,7.746,21.582,6.186z M10,15.464V8.536L16,12L10,15.464z"/>
                        </svg>
                    </i>-->

                    <!-- SHAREABLE LINK -->
                    <span @click.stop="getShareableLink">
                        <i class="material-icons mr-1 pl-2 pt-2 cursor-pointer icon-circled text-theme-discrete hover:text-theme-main" style="height: 34px; width: 34px; padding-left:8px; padding-top: 8px;">
                            <svg class="inline-svg-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18px" height="18px">
                                <path d="M21,12L14,5V9C7,10 4,15 3,20C5.5,16.5 9,14.9 14,14.9V19L21,12Z" />
                            </svg>
                        </i>
                    </span>

                </div>

            </div>
            <!-- END Stats and Sharing -->

            <!-- END Data Section -->

            <div id="comments-container" class="w-full h-full p-6 pb-24 text-theme-muted text-sm leading-normal" :class="{'bg-theme-level3': this.$root.shared.theme==='theme-dark'}">
                <div class="fb-comments" :data-href="commentsUrl" data-numposts="5" data-width=""></div>
            </div>


        </div>
        <!-- END Player View Data Container -->

        <div v-if="shareableLinkPanelVisible" class="absolute w-full h-full flex flex-col items-center justify-center" style="background-color: rgba(0,0,0,0.75); top: 0; left: 0; z-index: 9999;">
            <div class="bg-theme-level3 rounded-lg shadow-2xl p-6 flex flex-col" style="height: 16rem;">
                <span class="text-base text-theme-main font-bold mb-4">{{  translate('share-link-title') }}</span>
                <span class="text-sm text-theme-discrete font-bold mb-10">{{ translate('share-link-description') }}</span>
                <div class="text-sm text-theme-discrete mb-12">
                    <code>{{ shareableLinkCode }}</code>
                </div>
                <div class="flex flex-row items-center justify-center">
                    <button class="flex flex-row items-center justify-center text-base text-theme-control-label rounded-lg px-4 w-32 h-10"
                            :class="{'bg-theme-controls2 hover:bg-theme-controls1': !shareableLinkWasCopied && !shareableLinkHadError, 'bg-green-500 hover:bg-green-500': shareableLinkWasCopied, 'bg-red-500 hover:bg-red-500': shareableLinkHadError}"
                            @click.stop="copyLinkToClipboard">
                        {{ copy_label }}
                    </button>
                    <button class="ml-2 flex flex-row items-center justify-center text-base bg-theme-controls3 hover:bg-theme-controls2 text-theme-control-label rounded-lg px-4 w-32 h-10"
                            @click.stop="shareableLinkPanelVisible=false">
                        {{ translate('close') }}
                    </button>
                </div>
            </div>
        </div>

    </div>
    <!-- END Base Outer Element -->

</template>

<script>

    const Lang = require('lang.js');
    import translations from './messages.js';

    const lang = new Lang();

    lang.setMessages(translations);

    export default {

        data() {
            return {

                dataIsVisible: false,
/*
                vitorenes: this.$root.shared.playerPayload,

                duration: '...',
                plays: '---',
                comments: '---',
                likes: '---',
                dislikes: '---',

                publish_date: '...',

*/

                shareableLinkPanelVisible: false,

                copy_label: this.translate('copy'),
                shareableLinkWasCopied: false,
                shareableLinkHadError: false
            }
        },

        computed: {

            commentsUrl() {

                return this.baseurl + '/' + this.$root.shared.playerPayload.id;

            },

            shareableLinkCode() {

                return 'https://' + window.location.hostname + '/' + this.currentlocale + '/content/' + this.$root.shared.playerPayload.id;

            },

        },

        props: [
            'currentlocale',
            'baseurl'
        ],

        methods: {
            closePlayer() {
                /*console.log('Hiding the player...');*/
                Event.fire('hidePlayer');
            },

            toggleDataIsVisible() {

                this.dataIsVisible = !this.dataIsVisible;

            },

            translate(key) {
                key = 'strings.' + key;
                return lang.get(key);
            },

            getShareableLink() {
                this.shareableLinkPanelVisible=true;
            },

            setCopyLabelOk() {
                this.copy_label = this.translate('copied');
            },

            setCopyLabelError() {
                this.copy_label = 'Error!';
            },

            copyLinkToClipboard() {

                this.$copyText(this.shareableLinkCode).then((e) => {
                    this.setCopyLabelOk();
                    this.shareableLinkWasCopied=true;
                    console.log(e);
                }, (e) => {
                    this.setCopyLabelError();
                    this.shareableLinkHadError=true;
                    console.log(e)
                })
            }
        },

        mounted() {
            /*console.log('APP ROOT FROM PLAYERVIEW: ' + this.http.options.root);*/
            window.FB.XFBML.parse();
        }

    }

</script>

<style scoped>

    .icon-circled {
        background-color: var(--txt-copy-5);
    }

    .icon-circled:hover {
        background-color: var(--txt-copy-6);
        fill: var(--txt-main);
    }

    .inline-svg-icon {
        fill: var(--txt-discrete);
    }

    .inline-svg-icon:hover {
        fill: var(--txt-main);
    }

</style>
